import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert, Menu, MenuItem, ListItemIcon as MuiListItemIcon, Chip
} from '@mui/material';
import { Add, Edit, Delete, Settings, MoreVert, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllServiceConfigs, deleteServiceConfig } from '../../services/Api';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllServiceConfigs = () => {
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedConfig, setSelectedConfig] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchConfigs = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllServiceConfigs();
            if (response.success || Array.isArray(response.data)) {
                setConfigs(response.data || response);
            } else {
                setError(response.message || 'Failed to fetch configurations');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching configurations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfigs();
    }, []);

    const handleMenuOpen = (event, config) => {
        setAnchorEl(event.currentTarget);
        setSelectedConfig(config);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedConfig(null);
    };

    const handleDeleteClick = () => {
        setConfigToDelete(selectedConfig);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            setDeleteLoading(true);
            const response = await deleteServiceConfig(configToDelete.id);
            if (response.success) {
                setDeleteDialogOpen(false);
                fetchConfigs();
            } else {
                console.error(response.message || 'Failed to delete configuration');
            }
        } catch (err) {
            console.error('An error occurred while deleting:', err);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Settings sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>Service Configuration</Typography>
                        <Typography variant="body2" color="text.secondary">Global settings for service charges, GST, and service types.</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <IconButton onClick={fetchConfigs} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <Refresh />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/service-configs/create')}
                        sx={{ borderRadius: '8px' }}
                    >
                        New Configuration
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Service Charge</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>GST (%)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : configs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No configurations found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                configs.map((config, index) => (
                                    <TableRow key={config.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{config.type}</TableCell>
                                        <TableCell>₹{config.serviceCharge}</TableCell>
                                        <TableCell>{config.gstPercentage}%</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={config.isActive ? "Active" : "Inactive"} 
                                                size="small" 
                                                sx={{ 
                                                    bgcolor: config.isActive ? 'success.light' : 'grey.200',
                                                    color: config.isActive ? 'success.dark' : 'grey.800',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, config)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 150,
                        border: '1px solid',
                        borderColor: 'text.border',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => { navigate(`/service-configs/modify/${selectedConfig?.id}`, { state: { config: selectedConfig } }); handleMenuClose(); }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Configuration?"
                message={`Are you sure you want to delete the "${selectedConfig?.type}" configuration?`}
                loading={deleteLoading}
            />
        </Box>
    );
};

export default GetAllServiceConfigs;
